import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, PackageX } from "lucide-react";

const SEVERITY = {
  critique: { color: "bg-brand-red text-white", label: "Critique" },
  "élevé": { color: "bg-[#EE7A2A] text-white", label: "Élevé" },
  "modéré": { color: "bg-brand-yellow text-ink", label: "Modéré" },
  faible: { color: "bg-elevated text-mute", label: "Faible" },
};

export default function StockoutAlerts() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetch = async (d = days) => {
    setLoading(true);
    try {
      const { data: r } = await apiClient.get(`/my/restaurant/stockout-alerts?days=${d}`);
      setData(r);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(days); /* eslint-disable-next-line */ }, [days]);

  return (
    <div className="space-y-4" data-testid="stockout-alerts">
      <div className="bg-white border border-sand rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand-red font-semibold flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> {t("rdash.stockTitle")}
          </div>
          <div className="text-sm text-mute">{t("rdash.stockSub")}</div>
        </div>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`stockout-days-${d}`}
              className={`text-xs rounded-full px-3 py-1 border ${days === d ? "bg-ink text-white border-ink" : "bg-white text-ink border-sand"}`}
            >
              {t("rdash.daysUnit", { d })}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-mute text-sm py-6 text-center">{t("rdash.stockLoading")}</div>
      ) : !data || (data.alerts || []).length === 0 ? (
        <div className="bg-white border border-sand rounded-2xl p-8 text-center text-mute text-sm" data-testid="stockout-empty">
          <PackageX className="w-8 h-8 mx-auto mb-2 opacity-40" />
          {t("rdash.stockEmpty", { d: days })}
        </div>
      ) : (
        <div className="space-y-2">
          {data.alerts.map((a) => {
            const sev = SEVERITY[a.severity] || SEVERITY["faible"];
            return (
              <div key={a.dish_id} className="bg-white border border-sand rounded-2xl p-4" data-testid={`stockout-alert-${a.dish_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{a.dish_name}</div>
                    <div className="text-xs text-mute mt-0.5">
                      {t("rdash.stockRuptures", { count: a.count, days: a.distinct_days, date: a.last_date })}
                    </div>
                    <div className="text-sm mt-2 bg-elevated rounded-xl p-2">{a.recommendation}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${sev.color}`}>
                    {sev.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
